import { useIdentityContext } from '@flik/shared';
import { useReactQuery } from '@flik/shared/api';
import { useEffect, useRef, useState } from 'react';
import { StartFlikIcon } from './icons';
import {
  SearchContentProvider,
  SearchProviderContext,
  SearchResultItem,
} from './types';

/**
 * Internal hook to fetch previous sessions from sessions search API
 * This is used internally by the SessionProvider to fetch its data
 * Uses server-side search via the `keywords` parameter with debouncing
 *
 * @internal - This hook is not meant to be used outside of the session-provider
 */
function usePreviousSessionsInternal(searchQuery: string = '') {
  const { isLoggedIn } = useIdentityContext();
  const apiClient = useReactQuery();
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(searchQuery);
  const [sessions, setSessions] = useState<
    Array<{
      id: string;
      title: string;
    }>
  >([]);
  const [isLoading, setIsLoading] = useState(false);

  // Create mutation hook for searching sessions (must be at top level)
  const searchSessionsMutation = apiClient.useMutation(
    'post',
    '/v1/sessions/search',
  );

  // Use ref to track if we should ignore the response (for canceling previous requests)
  const requestIdRef = useRef(0);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Debounce the search query (500ms delay)
  useEffect(() => {
    // Clear any existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set new timer
    debounceTimerRef.current = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 500);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchQuery]);

  // Fetch sessions when debounced search query changes
  useEffect(() => {
    const trimmedQuery = debouncedSearchQuery.trim();

    // Only search if we have a query and user is logged in
    if (!isLoggedIn || trimmedQuery.length === 0) {
      setSessions([]);
      setIsLoading(false);
      return;
    }

    // Extract keywords from search query (split by spaces)
    const keywords = trimmedQuery
      .split(/\s+/)
      .filter((keyword) => keyword.length > 0);

    if (keywords.length === 0) {
      setSessions([]);
      setIsLoading(false);
      return;
    }

    // Increment request ID to cancel previous requests
    requestIdRef.current += 1;
    const currentRequestId = requestIdRef.current;

    setIsLoading(true);

    // Call the search API
    searchSessionsMutation
      .mutateAsync({
        body: {
          keywords,
          limit: 10, // Limit to 10 results
        },
      })
      .then((response) => {
        // Only update state if this is still the latest request
        if (currentRequestId === requestIdRef.current) {
          const items = response.sessions?.items || [];
          setSessions(
            items.map((session) => ({
              id: session.id,
              title: session.title,
            })),
          );
          setIsLoading(false);
        }
      })
      .catch((error) => {
        // Only handle error if this is still the latest request
        if (currentRequestId === requestIdRef.current) {
          console.error('Error searching sessions:', error);
          setSessions([]);
          setIsLoading(false);
        }
      });
  }, [debouncedSearchQuery, isLoggedIn]); // Removed searchSessionsMutation from deps

  return {
    previousSessions: sessions,
    isLoading,
  };
}

/**
 * Hook to enrich search context with previous sessions data
 * This encapsulates the data fetching logic so SearchContent doesn't need to know about it
 */
export function useEnrichedSearchContextWithSessions(
  context: SearchProviderContext,
): SearchProviderContext {
  const { previousSessions, isLoading } = usePreviousSessionsInternal(
    context.searchQuery,
  );

  return {
    ...context,
    previousSessions,
    isLoadingSessions: isLoading,
  };
}

export class SessionProvider implements SearchContentProvider {
  id = 'session-provider';
  name = 'Session Provider';
  priority = 20; // Lower priority than system items, similar to user provider

  isLoading(context: SearchProviderContext): boolean {
    // The loading state is stored in the context by useEnrichedSearchContextWithSessions
    return context.isLoadingSessions ?? false;
  }

  getResults(context: SearchProviderContext): SearchResultItem[] {
    const { searchQuery, previousSessions } = context;

    const results: SearchResultItem[] = [];

    // Only show results if we have a search query and previous sessions
    if (
      !searchQuery.trim() ||
      !previousSessions ||
      previousSessions.length === 0
    ) {
      return results;
    }

    // Process previous sessions
    // Note: API already filtered these results server-side, so we skip client-side query filtering
    previousSessions.forEach((session) => {
      results.push({
        id: `session-${session.id}`,
        title: session.title,
        type: 'session', // Use 'session' type to show in "Sessions" section
        priority: 20,
        icon: <StartFlikIcon />,
        skipQueryFiltering: true, // API already filtered server-side, trust the results
        externalUrl: `/sessions/${session.id}`, // Open session page when clicked
      });
    });

    return results;
  }

  isActive(context: SearchProviderContext): boolean {
    return context.isLoggedIn;
  }
}
